import {
  CONVEYANCE_TYPES,
  createRedemption,
  defaultStoredAddress,
  deleteMessage,
  deleteStoredAddress,
  fetchLoyaltyState,
  fetchMessages,
  fetchRedeemables,
  fetchRedemptions,
  fetchRewardsHistory,
  getRewards,
  getStoredAddresses,
  getUser,
  getSavedCards,
  markMessageAsRead,
  redeemReceiptBarcode,
  updateMe,
  favoriteOrders,
  pastOrders,
  favoritesSave,
  favoritesDelete,
  setConveyance,
  getFavoriteLocations,
  deleteFavoriteLocation,
  saveFavoriteLocation,
  validateLoyaltyToken,
} from '@koala/sdk/v4';
import { encode } from 'html-entities';
import Router from 'next/router';
import { all, call, put, select, takeLatest, type SagaReturnType } from 'redux-saga/effects';
import actions from './actions';
import { genericEventHandler } from '@/analytics/events';
import { GlobalEvents } from '@/analytics/events/constants';
import { SESSION_ENDED_ORDER_HISTORY_COPY } from '@/constants/authentication';
import { ERROR_MESSAGES, API_CONVEYANCE_TYPES } from '@/constants/events';
import {
  FETCH_ORDER_HISTORY_ERROR_COPY,
  FETCH_POINTS_HISTORY_ERROR_COPY,
  FETCH_REDEMPTIONS_ERROR_COPY,
  FETCH_REWARDS_ERROR_COPY,
  LOYALTY_FEATURES,
  REWARD_TYPE,
} from '@/constants/loyalty';
import { ROUTES } from '@/constants/routes';
import globalActions from '@/redux/global/actions';
import { locationsActions } from '@/redux/locations/actions';
import basketActions from '@/redux/basket/actions';
import { surfaceConveyanceGatewayFailure } from '@/redux/conveyanceMode/gatewayFailure';
import orderStatusActions from '@/redux/orderStatus/actions';
import { createHttpClient } from '@/services/client';
import { type RootState } from '@/types/app';
import { getOrigin } from '@/utils';
import { determineBasketFulfillment } from '@/utils/basket';
import {
  assembleDeliveryObjectFromState,
  getIdsFromState,
  getInitializationErrorType,
} from '@/utils/checkout';
import { getErrorDataObject } from '@/utils/errorEnvelope';
import { deriveReferrer, prepareErrorMessage } from '@/utils/global';
import { getLoyaltyInfoFromState } from '@/utils/loyalty';
import { fetchOrderDetails } from '@/utils/orders';
import { safelyGetString } from '@/utils/stringHelpers';
import * as ErrorReporter from '@/utils/errorReporter';
import { GATEWAY_STATUSES, isNetworkError, isStatusError } from '@/utils/networkErrors';
import { TRANSPORT_RETRY_ATTEMPTS, retryOnceOnStatuses, retryWithBackoff } from '@/utils/requests';

export function* fetchMeSaga() {
  try {
    const origin = getOrigin(window.location.host);

    /*
     * If we don't have a loyalty token (aka: a token with a `refresh_token` key), we should
     * skip this saga, otherwise we are requesting a ton of unnecessary api calls from the ordering api
     */
    if (!validateLoyaltyToken(origin)) {
      yield put({ type: actions.ME_STOP_LOADING });
      return;
    }

    const client = createHttpClient({ origin });

    // Runs on every page (route change and `window.onload`), and fails as an iOS "Load failed"
    // when the connection drops for a moment (COO-WEBAPP-55V). Retry only that transport failure,
    // over ~6 s; a 401 or 5xx still fails on the first answer, as before. (TEAM-15682)
    const response: SagaReturnType<typeof getUser> = yield* retryWithBackoff(
      getUser,
      { attempts: 1, transportAttempts: TRANSPORT_RETRY_ATTEMPTS },
      { client },
    );

    /*
     * `fetchMe` will return undefined if there is no matching user, so we should bail out of this
     * saga to avoid more unnecessary calls
     */
    if (response.data === undefined) {
      yield put({ type: actions.ME_STOP_LOADING });
      return;
    }

    const state: RootState = yield select();
    const reordersEnabled = state.app.cmsConfig.webConfig.accounts.reorders;
    const androidReordersEnabled = state.app.cmsConfig.webConfig.android_features.reorders;

    yield all([
      put({ type: actions.FETCH_LOYALTY_STATE }),
      put({ type: actions.FETCH_MESSAGES }),
      put({ type: actions.FETCH_REDEEMABLES }),
      put({ type: actions.FETCH_OFFERS }),
    ]);

    // Fetch past orders if reorders are enabled
    // TODO: API does not yet allow us to limit/paginate results
    if (reordersEnabled || androidReordersEnabled) {
      yield put({ type: actions.FETCH_ME_PAST_ORDERS });
    }

    // Success
    yield put({ type: actions.ME_SUCCESS, data: response.data });
  } catch (error) {
    // `me.data` is not persisted, so after a reload the profile-backed /account sections
    // (contact info, birthday, and Wallet — whose stored-cards query is gated on the user
    // id) wait on this request. A network-layer failure here used to leave them blank with
    // no message and no retry, and unreported (TEAM-14780 QA, AC1). Flag it so the page
    // can show the shared connection-error state instead.
    if (isNetworkError(error)) {
      // `_app` re-dispatches FETCH_ME on every route change, so while the diner stays offline
      // this fails once per navigation. Report the first failure of an outage only; the flag
      // is lowered by the next profile outcome, which re-arms reporting.
      const alreadyFlagged: boolean = yield select(
        (state: RootState) => state.app.me.meConnectionError,
      );
      yield put(actions.fetchMeConnectionError());
      if (!alreadyFlagged) {
        ErrorReporter.captureException(error, 'error', {
          eventName: ERROR_MESSAGES.FETCH_USER_DETAILS_ERROR,
        });
      }
      return;
    }
    // `fromProfile` marks this as the profile fetch's own failure: only it may lower
    // `meConnectionError`. `fetchMeFavoriteLocationsSaga` also dispatches ME_FAIL (saga.ts,
    // its non-network branch), and letting that clear the flag hid the /account panel,
    // remounted the sections, and restarted the failing fetches in a loop (code review run 5).
    yield put({ type: actions.ME_FAIL, fromProfile: true });
  }
}

export function* fetchMeFavoriteLocationsSaga() {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    const response: SagaReturnType<typeof getFavoriteLocations> = yield call(getFavoriteLocations, {
      client,
    });

    yield put(actions.fetchMeFavoriteLocationsSuccess(response.data));

    // Set default active location. A default, not the diner's pick, so the store finder's next
    // fetch still replaces it with the nearest open location (TEAM-13911).
    yield put(locationsActions.setDefaultActiveLocation(response.data?.[0]?.id ?? null));
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error fetching your favorite locations.',
      error,
    );

    // A network-layer failure now drives the shared connection-error state (with a
    // retry) in place of the "no favorites yet" empty state — the generic toast was
    // the only signal before and the list still read as empty (TEAM-14780). Other
    // failures keep the existing toast; the panel copy is connection-specific.
    if (isNetworkError(error)) {
      yield put(actions.meFavoriteLocationsConnectionError());
    } else {
      yield put({ type: actions.ME_FAIL });
      yield put(globalActions.displayErrorToast(errorResponse.message, true));
    }

    // KA event
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.FETCH_FAVORITE_LOCATIONS_ERROR,
    });
  }
}

function* updateMeSaga(action: ReturnType<typeof actions.updateMe>) {
  // Delete all null keys from payload before submitting
  const updatedParams = Object.assign({}, action.params);
  Object.keys(updatedParams).forEach(
    // @ts-expect-error `key` can't index `LoyaltyUserUpdateInfo`.
    (key) => updatedParams[key] === null && delete updatedParams[key],
  );

  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    yield call(updateMe, updatedParams, { client });

    yield put(actions.updateMeSuccess());
    yield put(actions.fetchMe());
    yield put(globalActions.displayToast('Your profile was successfully updated!'));
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error updating your profile.',
      error,
    );
    /**
     * TEAM-13882: a NAMED EXCEPTION to "never show the backend's text". `error_data.failures` on a
     * `bad_request` is per-field form validation ("The phone number field is required.") — copy the
     * diner can act on, unlike `error_description`. It stays.
     *
     * This also fixes a double render: `deriveErrorMessage` used to join `failures` into the message
     * as well, so the diner saw every field error twice. The join now lives only in `detail`, making
     * this append the single source. The strings are HTML-escaped because the toast renders with
     * `dangerouslySetInnerHTML` and these values originate upstream.
     */
    let errorMessage = errorResponse.message;
    const failures = getErrorDataObject(errorResponse.error)?.failures;
    if (errorResponse?.error?.error === 'bad_request' && failures) {
      const fieldErrors = Object.values(failures)
        .flat()
        .map((fieldError) => encode(fieldError))
        .join(', ');
      if (fieldErrors) {
        errorMessage += `<br/>${fieldErrors}`;
      }
    }
    yield put(actions.updateMeFail());
    yield put(globalActions.displayErrorToast(errorMessage));
    ErrorReporter.captureException(error, 'error', { errorResponse });
  }
}

export function* appendUserPhoneSaga(action: ReturnType<typeof actions.appendUserPhone>) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    const state: RootState = yield select();
    const { basketId, locationId } = getIdsFromState(state);
    const organization = state.app.organization;

    // Determine checkout order fulfillment type
    const fulfillmentType = determineBasketFulfillment(state.app.basket.fulfillment?.address);

    // If loyalty is enabled
    if (organization.organization.loyalty_driver_id) {
      // Silently attempt to update the user
      yield call(updateMe, { phone: action.phone }, { client });

      // Right now we're just re-firing on deliveries as we only bail out
      // of the inializeOrder saga if it's a delivery with no phone number
      if (fulfillmentType === CONVEYANCE_TYPES.DELIVERY) {
        const deliveryAddress = assembleDeliveryObjectFromState(state);

        let response: SagaReturnType<typeof setConveyance>;
        try {
          // TEAM-15341: same single gateway retry as the other setConveyance sites.
          response = yield* retryOnceOnStatuses(
            setConveyance,
            { statuses: GATEWAY_STATUSES },
            {
              basketId,
              locationId,
              type: API_CONVEYANCE_TYPES.DELIVERY,
              // @ts-expect-error `assembleDeliveryObjectFromState` returns the wider address type.
              data: deliveryAddress,
            },
            { client },
          );
        } catch (conveyanceError) {
          // A second 502 left the delivery handoff unsaved: show the cart-preserving conveyance
          // recovery modal (and report it) rather than the reconciliation modal below. Any other
          // failure keeps the existing handling.
          if (isStatusError(conveyanceError, GATEWAY_STATUSES)) {
            yield* surfaceConveyanceGatewayFailure(conveyanceError);
            return;
          }
          throw conveyanceError;
        }

        yield put(basketActions.success(response));
      }
    }
  } catch (error) {
    // Silently log error
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      null,
      error,
    );

    // Keep the time picker and phone form available even when diner copy is generic.
    yield put(
      orderStatusActions.initializeOrderReconciliation(
        errorResponse.message,
        getInitializationErrorType(errorResponse.detail),
      ),
    );
    const errorEvent =
      errorResponse?.error?.error === 'unable_to_update_user'
        ? ERROR_MESSAGES.ADD_USER_PHONE_ERROR
        : ERROR_MESSAGES.INIT_CHECKOUT_UNHANDLED_CONVEYANCE_FAILURE;

    ErrorReporter.captureException(error, 'error', { errorResponse, eventName: errorEvent });
  }
}

export function* fetchMeFavoriteOrdersSaga() {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    const response: SagaReturnType<typeof favoriteOrders> = yield call(favoriteOrders, { client });

    yield put(actions.fetchMeFavoriteOrdersSuccess(response.data));
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error fetching your favorite orders.',
      error,
    );

    // The favorites page renders this list's "no favorite orders yet" empty state even
    // when the fetch failed, so a dropped connection was indistinguishable from genuinely
    // having none — the toast was the only signal (TEAM-14780). Flag a network-layer
    // failure so the list can show the shared connection-error state with a retry, and
    // suppress the toast in that branch so the panel is the single signal. Other failures
    // keep the toast; the panel copy is connection-specific.
    if (isNetworkError(error)) {
      yield put(actions.fetchMeFavoriteOrdersConnectionError());
    } else {
      // The toast is the signal here, but it cannot be the only dispatch: this list now
      // renders nothing while `favoriteOrdersLoading` is up, and only SUCCESS and the
      // connection-error action lower it. A 401/5xx that dispatched the toast alone left
      // the section permanently blank (TEAM-14780).
      yield put(actions.fetchMeFavoriteOrdersFail());
      yield put(globalActions.displayErrorToast(errorResponse.message, true));
    }

    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.FETCH_FAVORITE_ORDERS_ERROR,
    });
  }
}

/**
 * The statuses coo-api answers `/me/orders` with when the request carries no diner session —
 * a client-only token gets a 403 `access_denied`, an invalid one a 401 (TEAM-14520).
 */
const AUTH_REFUSAL_STATUSES: readonly number[] = [401, 403];

/**
 * The session is over only once the SDK has given up on it: a refused refresh swaps the loyalty
 * token for a client token. While a loyalty token is still held, a 401/403 is not proof the diner
 * is signed out, so it never signs them out. And only on the account pages — `fetchMeSaga` (every route change, with
 * reorders on) and `ReorderList` also fetch past orders from store and checkout pages, where
 * pulling the diner to /login mid-order would be worse than the toast.
 */
const sessionEndedOnAccountPage = (origin: string) =>
  (Router.pathname?.startsWith(ROUTES.ACCOUNT) ?? false) && !validateLoyaltyToken(origin);

function* endOrderHistorySession(extra: Record<string, unknown>) {
  yield put(actions.clearMe());
  // The applied rewards belong to the ended session; a diner who carries on as a guest could not
  // redeem them at checkout.
  yield put(basketActions.depersonalizeBasket());
  yield put(globalActions.displayErrorToast(SESSION_ENDED_ORDER_HISTORY_COPY, true));
  void Router.push(`${ROUTES.LOGIN}?referrer=${deriveReferrer(Router)}`);

  ErrorReporter.captureMessage(
    `${ERROR_MESSAGES.FETCH_ORDER_HISTORY_ERROR} (session ended)`,
    'info',
    extra,
  );
}

/**
 * A refused refresh makes the SDK resolve with a non-list instead of throwing (`client.ts`). The
 * order pages read `pastOrders.length`, so it is never stored and is handled like a refusal.
 */
class NoOrderListError extends Error {}

export function* fetchMePastOrdersSaga() {
  const origin = getOrigin(window.location.host);

  // Order history is signed-in only: without a loyalty token coo-api can only refuse it (TEAM-14520).
  if (!validateLoyaltyToken(origin)) {
    return;
  }

  try {
    const client = createHttpClient({ origin });

    const response: SagaReturnType<typeof pastOrders> = yield* retryWithBackoff(
      pastOrders,
      {},
      { client },
    );

    if (!Array.isArray(response?.data)) {
      throw new NoOrderListError(`${ERROR_MESSAGES.FETCH_ORDER_HISTORY_ERROR}: no order list`);
    }
    yield put(actions.fetchMePastOrdersSuccess(response.data));
  } catch (error) {
    const noOrderList = error instanceof NoOrderListError;

    if (
      (noOrderList || isStatusError(error, AUTH_REFUSAL_STATUSES)) &&
      sessionEndedOnAccountPage(origin)
    ) {
      yield* endOrderHistorySession(
        noOrderList
          ? { reason: 'refresh refused' }
          : { status: (error as { status?: number }).status },
      );
      return;
    }

    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      FETCH_ORDER_HISTORY_ERROR_COPY,
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.FETCH_ORDER_HISTORY_ERROR,
    });
  }
}

function* fetchAddressesSaga() {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    const response: SagaReturnType<typeof getStoredAddresses> = yield call(getStoredAddresses, {
      client,
    });
    yield put({
      type: actions.FETCH_ADDRESSES_SUCCESS,
      myAddresses: response.data,
    });
  } catch (error) {
    // KA event
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error fetching your addresses.',
      error,
    );

    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.FETCH_STORED_ADDRESSES_ERROR,
    });
  }
}

function* deleteAddressSaga(action: ReturnType<typeof actions.deleteAddress>) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    yield put(actions.toggleStoredAddressesMenu(false));
    yield call(deleteStoredAddress, action.id, { client });
    yield put({ type: actions.FETCH_ADDRESSES });

    // Success message
    const state: RootState = yield select();
    const strings = state.app.cmsConfig.strings;
    const successString = safelyGetString(strings, 'account.addresses_delete_success_message');
    yield put(globalActions.displayToast(successString));

    genericEventHandler(GlobalEvents.ACCOUNT__STORED_ADDRESS_DELETED, {
      name: action.id.toString(),
    });
  } catch (error) {
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error deleting your address. Please try again.',
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.DELETE_STORED_ADDRESS_ERROR,
    });
  }
}

function* defaultAddressSaga(action: ReturnType<typeof actions.defaultAddress>) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    yield put(actions.toggleStoredAddressesMenu(false));
    yield call(defaultStoredAddress, action.id, { client });
    yield put({ type: actions.FETCH_ADDRESSES });

    // Success message
    const state: RootState = yield select();
    const strings = state.app.cmsConfig.strings;
    const successString = safelyGetString(strings, 'account.addresses_set_default_success_message');
    yield put(globalActions.displayToast(successString));
  } catch (error) {
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error setting your default address. Please try again.',
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.SET_DEFAULT_STORED_ADDRESS_ERROR,
    });
  }
}

function* fetchAllLocationsCreditCardsSaga(
  action: ReturnType<typeof actions.fetchAllLocationsCreditCards>,
) {
  try {
    const pastOrders = action.pastOrders.slice(0);
    const origin = getOrigin(window.location.host);
    const client = createHttpClient({ origin });

    /** @TODO improve `locationBaskets` type. */
    const locationBaskets = {};
    const locationsCreditCards = [];
    const foundCards: string[] = [];
    for (const order of pastOrders) {
      // @ts-expect-error
      const key = `basket_${order.store_location_id}`;
      // Only fetch cards for each location
      // @ts-expect-error `string` can't index `{}`.
      if (!locationBaskets[key]) {
        const savedCards: SagaReturnType<typeof getSavedCards> = yield call(
          getSavedCards,
          { client },
          // @ts-expect-error order_data is mistyped.
          order.order_data.basket.id,
        );
        // @ts-expect-error
        locationBaskets[key] = order.order_data.basket.id;

        // If the location has cards
        if (savedCards.length) {
          const locationCreditCards = {
            // @ts-expect-error
            locationLabel: order.store_location.label,
            // @ts-expect-error
            basketId: order.order_data.basket.id,
            savedCards: savedCards.filter((card) => {
              // Only add creditcard type that hasn't been found yet.
              if (card.type === 'creditcard') {
                if (foundCards.includes(card.id)) {
                  return false;
                }
                foundCards.push(card.id);
                return true;
              }
            }),
          };
          // If after filtering there's still cards for the location.
          if (locationCreditCards.savedCards.length) {
            locationsCreditCards.push(locationCreditCards);
          }
        }
      }
      // One basket id is enough now.
      break;
    }
    yield put(actions.fetchAllLocationsCreditCardsSuccess(locationsCreditCards));
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error fetching your payment information.',
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.FETCH_ALL_LOCATIONS_CC_ERROR,
    });
  }
}

function* fetchMeOrderDetailsSaga(action: ReturnType<typeof actions.fetchMeOrderDetails>) {
  try {
    const response: SagaReturnType<typeof fetchOrderDetails> = yield call(
      fetchOrderDetails,
      action.id,
      action.pastOrders,
    );

    // @ts-expect-error
    yield put(actions.fetchMeOrderDetailsSuccess(response));
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error fetching your order details.',
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    // Error Reporting
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.FETCH_ORDER_DETAILS_ERROR,
    });
  }
}

function* deleteFavoriteLocationSaga(action: ReturnType<typeof actions.removeFavoriteLocation>) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    yield call(deleteFavoriteLocation, action.id, { client });
    yield put(actions.deleteMeFavoriteLocationSuccess());
    yield put(actions.fetchMeFavoriteLocations());

    genericEventHandler(GlobalEvents.ACCOUNT__REMOVE_FAVORITE_LOCATION, {
      name: action.label,
      // @ts-expect-error
      details: action.id,
    });
  } catch (error) {
    yield put(actions.deleteMeFavoriteLocationFail());
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error unfavoriting this location.',
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    // Error Reporting
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.UNFAVORITE_LOCATION_ERROR,
    });
  }
}

function* addFavoriteLocationSaga(action: ReturnType<typeof actions.addFavoriteLocation>) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    yield call(saveFavoriteLocation, action.id, { client });
    yield call(fetchMeFavoriteLocationsSaga);

    const state: RootState = yield select();
    const locations = state.app.locations.list;
    /** @TODO ensure that `location` is defined. */
    // @ts-expect-error
    const locationLabel = locations.find((location) => location.id === action.id).label;

    genericEventHandler(GlobalEvents.ACCOUNT__ADD_FAVORITE_LOCATION, {
      name: locationLabel,
      // @ts-expect-error
      details: action.id,
    });
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error favoriting this location.',
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    // Error Reporting
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.FAVORITE_LOCATION_ERROR,
    });
  }
}

function* deleteFavoriteOrderSaga(action: ReturnType<typeof actions.removeFavoriteOrder>) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    yield call(favoritesDelete, action.id, { client });
    yield put(actions.deleteMeFavoriteOrderSuccess());
    yield put(actions.fetchMeFavoriteOrders());
    yield put(
      globalActions.displayToast('This order was successfully removed from your favorites!'),
    );
  } catch (error) {
    yield put(actions.deleteMeFavoriteOrderFail());
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error unfavoriting this order.',
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    // Error Reporting
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.UNFAVORITE_ORDER_ERROR,
    });
  }
}

function* addFavoriteOrderSaga(action: ReturnType<typeof actions.addFavoriteOrder>) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    yield call(favoritesSave, action.id, { client });
    yield call(fetchMeFavoriteOrdersSaga);
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error favoriting this order.',
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    // Error Reporting
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.FAVORITE_ORDER_ERROR,
    });
  }
}

function* fetchOffersSaga() {
  const { loyaltyDriverId }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyDriverId) {
    return;
  }

  // Only fetch offers if feature supported
  const { loyaltyFeatureFlags }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyFeatureFlags?.[LOYALTY_FEATURES.INDEX_MY_REWARDS]) {
    return;
  }

  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    const response: SagaReturnType<typeof getRewards> = yield call(getRewards, {
      client,
    });

    // Success
    yield put({ type: actions.FETCH_OFFERS_SUCCESS, rewards: response.data });
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      FETCH_REWARDS_ERROR_COPY,
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.FETCH_REWARDS_ERROR,
    });
  }
}

/*
 * Deliberately NOT suppressing the loyalty fetches' toasts on a network-layer failure.
 *
 * Doing so was the obvious way to stop up to three differently-worded, dismiss-only
 * toasts stacking on top of the rewards connection banner (TEAM-14780 AC4), and it was
 * tried. It is wrong: `loyaltyConnectionError` is written only by `fetchLoyaltyStateSaga`,
 * so the banner appears only when the loyalty-STATE fetch is the one that failed — while
 * `fetchOffersSaga` / `fetchRedeemablesSaga` / `fetchRewardsHistorySaga` are also
 * dispatched from `fetchMeSaga` (every page, for any signed-in diner),
 * `createRedemptionSagaSuccess`, the points-history section and the social-causes modal.
 * Suppressing there removes the only signal those paths have and leaves the diner with
 * nothing — the defect this ticket exists to remove.
 *
 * De-duplicating is done on the surface instead: the rewards page drops these toasts
 * (matched on the exported `*_ERROR_COPY` constants) only while it is itself showing the
 * connection-error state (`pages/account/rewards/index.tsx`). Everywhere else they fire.
 */
// Punchh V2 only
export function* fetchLoyaltyStateSaga() {
  const { loyaltyDriverId }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyDriverId) {
    return;
  }

  // Only fetch loyalty state if supported
  const { loyaltyFeatureFlags }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyFeatureFlags?.[LOYALTY_FEATURES.SHOW_LOYALTY_STATE]) {
    return;
  }

  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    const response: SagaReturnType<typeof fetchLoyaltyState> = yield* retryWithBackoff(
      fetchLoyaltyState,
      {},
      { client },
    );

    // Success
    yield put({
      type: actions.FETCH_LOYALTY_STATE_SUCCESS,
      data: response.data,
    });
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error fetching loyalty state.',
      error,
    );

    // The rewards page gates all content on `loyaltyState`, so a failed fetch left
    // it blank with no signal. Flag a network-layer failure so the page can show the
    // shared connection-error state with a retry instead of nothing (TEAM-14780).
    if (isNetworkError(error)) {
      yield put(actions.fetchLoyaltyStateConnectionError());
    } else {
      yield put(actions.fetchLoyaltyStateFail());
    }

    // Error Reporting
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.FETCH_LOYALTY_STATE_ERROR,
    });
  }
}

function* fetchRedeemablesSaga() {
  const { loyaltyDriverId }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyDriverId) {
    return;
  }

  // Only fetch redeemables if feature supported
  const { loyaltyFeatureFlags }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyFeatureFlags?.[LOYALTY_FEATURES.INDEX_REDEEMABLES]) {
    return;
  }

  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    // TEAM-14558: retry with jittered backoff, matching the loyalty state call's
    // existing pattern. Previously this was a plain yield call() with no retry.
    const response: SagaReturnType<typeof fetchRedeemables> = yield* retryWithBackoff(
      fetchRedeemables,
      {},
      { client },
    );

    // Success
    yield put({
      type: actions.FETCH_REDEEMABLES_SUCCESS,
      redeemables: response.data,
    });
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      FETCH_REWARDS_ERROR_COPY,
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    // Error Reporting
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.FETCH_REWARDS_ERROR,
    });
  }
}

function* fetchMessagesSaga() {
  const { loyaltyDriverId }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyDriverId) {
    return;
  }

  // Only fetch messages if feature supported
  const { loyaltyFeatureFlags }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyFeatureFlags?.[LOYALTY_FEATURES.INDEX_LOYALTY_MESSAGES]) {
    return;
  }

  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    const response: SagaReturnType<typeof fetchMessages> = yield call(fetchMessages, { client });

    // Success
    yield put({
      type: actions.FETCH_MESSAGES_SUCCESS,
      messages: response.data,
    });
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error fetching your messages.',
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    // Error Reporting
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.FETCH_MESSAGES_ERROR,
    });
  }
}

function* fetchRewardsHistorySaga() {
  const { loyaltyDriverId }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyDriverId) {
    return;
  }

  // Only fetch rewards history if feature supported
  const { loyaltyFeatureFlags }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyFeatureFlags?.[LOYALTY_FEATURES.SHOW_LOYALTY_HISTORY]) {
    return;
  }

  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    const response: SagaReturnType<typeof fetchRewardsHistory> = yield call(fetchRewardsHistory, {
      client,
    });
    // Success
    yield put({
      type: actions.FETCH_REWARDS_HISTORY_SUCCESS,
      rewardsHistory: response.data,
    });
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      FETCH_POINTS_HISTORY_ERROR_COPY,
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    // Error Reporting
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.FETCH_REWARDS_HISTORY_ERROR,
    });
  }
}

function* markMessageAsReadSaga(action: ReturnType<typeof actions.markMessageAsRead>) {
  const { loyaltyDriverId }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyDriverId) {
    return;
  }

  // Only mark as read if feature supported
  const { loyaltyFeatureFlags }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyFeatureFlags?.[LOYALTY_FEATURES.MARK_READ_LOYALTY_MESSAGE]) {
    return;
  }

  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    yield call(markMessageAsRead, action.messageId, { client });
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error marking message as read.',
      error,
    );

    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.MARK_AS_READ_ERROR,
    });
  }

  // Always re-fetch messages in case some error happened that resets the read at state
  yield put({ type: actions.FETCH_MESSAGES });
}

function* deleteMessageSaga(action: ReturnType<typeof actions.deleteMessage>) {
  const { loyaltyDriverId }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyDriverId) {
    return;
  }

  // Only allow delete message if feature supported
  const { loyaltyFeatureFlags }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyFeatureFlags?.[LOYALTY_FEATURES.DESTROY_LOYALTY_MESSAGE]) {
    return;
  }

  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    yield call(deleteMessage, action.messageId, { client });

    // Fetch messages on successs
    yield put({ type: actions.FETCH_MESSAGES });
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error deleting message.',
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.DELETE_MESSAGE_ERROR,
    });
  }
}

function* createRedemptionSagaSuccess(_action: ReturnType<typeof actions.createRedemptionSuccess>) {
  try {
    // Fetch redemptions, loyalty state, and rewards history on successs
    yield all([
      put({ type: actions.FETCH_REDEMPTIONS }),
      put({ type: actions.FETCH_LOYALTY_STATE }),
      put({ type: actions.FETCH_REWARDS_HISTORY }),
    ]);
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'Encountered an unexpected error while fetching redemptions, loyalty state and rewards history',
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));
  }
}

function* createRedemptionFailureSaga(action: ReturnType<typeof actions.createRedemptionFailure>) {
  // Error Notification
  const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
    prepareErrorMessage,
    'There was an error redeeming reward.',
    action.error,
  );

  yield put(globalActions.displayErrorToast(errorResponse.message, true));

  // Determine whether to fire the Reward or Offer analytics event
  const redemptionErrorEventLabel =
    action.rewardType === REWARD_TYPE.OFFER
      ? ERROR_MESSAGES.REDEEM_OFFER_ERROR
      : ERROR_MESSAGES.REDEEM_REWARD_ERROR;

  ErrorReporter.captureException(action.error, 'error', {
    errorResponse,
    eventName: redemptionErrorEventLabel,
  });
}

function* createRedemptionSaga(action: ReturnType<typeof actions.createRedemption>) {
  const { loyaltyDriverId }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  const { loyaltyFeatureFlags }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);

  if (!loyaltyDriverId) {
    return;
  }

  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    // First, see if our reward/offer has already been redeemed before creating a new redemption

    /* If the reward is an offer */
    if (action.rewardType === REWARD_TYPE.OFFER) {
      // Only allow offer redemption if feature supported
      if (
        /** @TODO ensure that `CREATE_REDEMPTION_USING_REWARD` flag is defined. */
        // @ts-expect-error
        !loyaltyFeatureFlags?.[LOYALTY_FEATURES.CREATE_REDEMPTION_USING_REWARD]
      ) {
        return;
      }

      const myOffers: SagaReturnType<typeof getRewards> = yield call(getRewards, { client });

      // Find our offer from the offers array
      const foundOffer = myOffers.data.filter((offer) => offer.id === action.rewardId);

      // Get tracking code from our found offer
      const existingOfferRedemption = foundOffer.length > 0 && foundOffer[0].redemption;

      // Display the tracking code
      if (existingOfferRedemption) {
        yield put({
          type: actions.CREATE_REDEMPTION_SUCCESS,
          activeRedemption: existingOfferRedemption,
        });

        return;
      }
    }

    /* If the reward is a redeemable */
    if (action.rewardType === REWARD_TYPE.REWARD) {
      // Only allow reward redemption if feature supported
      if (
        /** @TODO ensure that `CREATE_REDEMPTION_USING_REDEEMABLE` flag is defined. */
        // @ts-expect-error
        !loyaltyFeatureFlags?.[LOYALTY_FEATURES.CREATE_REDEMPTION_USING_REDEEMABLE] ||
        !loyaltyFeatureFlags?.[LOYALTY_FEATURES.INDEX_REDEMPTIONS]
      ) {
        return;
      }

      // Fetch redemptions
      const redemptions: SagaReturnType<typeof fetchRedemptions> = yield call(fetchRedemptions, {
        client,
      });

      // Find out if our reward was previously redeemed
      const existingRedemption = redemptions.data.find(
        (redemption) => redemption.redeemable_id === +action.rewardId,
      );

      // If so, set active redemption
      if (existingRedemption) {
        yield put({
          type: actions.CREATE_REDEMPTION_SUCCESS,
          activeRedemption: existingRedemption,
        });

        return;
      }
    }

    const newRedemption: SagaReturnType<typeof createRedemption> = yield call(
      createRedemption,
      action.rewardId,
      action.rewardType,
      { client },
    );

    yield put({
      type: actions.CREATE_REDEMPTION_SUCCESS,
      activeRedemption: newRedemption,
      rewardType: action.rewardType,
    });
  } catch (error) {
    yield put({
      type: actions.CREATE_REDEMPTION_FAILURE,
      error,
      rewardType: action.rewardType,
    });
  }
}

function* fetchRedemptionsSaga() {
  const { loyaltyDriverId }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyDriverId) {
    return;
  }

  // Only fetch redemptions if feature supported
  const { loyaltyFeatureFlags }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyFeatureFlags?.[LOYALTY_FEATURES.INDEX_REDEMPTIONS]) {
    return;
  }

  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    const response: SagaReturnType<typeof fetchRedemptions> = yield call(fetchRedemptions, {
      client,
    });

    // Fetch redemptions on successs
    yield put({
      type: actions.FETCH_REDEMPTIONS_SUCCESS,
      redemptions: response.data,
    });
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      FETCH_REDEMPTIONS_ERROR_COPY,
      error,
    );

    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.FETCH_REDEMPTIONS_ERROR,
    });
  }
}

function* redeemReceiptBarcodeSaga(action: ReturnType<typeof actions.redeemReceiptBarcode>) {
  const { loyaltyDriverId }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyDriverId) {
    return;
  }

  // Only allow barcode redemption if feature supported
  const { loyaltyFeatureFlags }: ReturnType<typeof getLoyaltyInfoFromState> =
    yield select(getLoyaltyInfoFromState);
  if (!loyaltyFeatureFlags?.[LOYALTY_FEATURES.CLAIM_REWARDS_BY_RECEIPT]) {
    return;
  }

  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    // @ts-expect-error
    const { points_earned }: SagaReturnType<typeof redeemReceiptBarcode> = yield call(
      redeemReceiptBarcode,
      action.barcode,
      { client },
    );

    // Success Notification
    if (points_earned > 0) {
      const state: RootState = yield select();
      const strings = state.app.cmsConfig.strings;
      const successString = safelyGetString(strings, 'rewards.barcode_success_message', {
        points: points_earned,
      });
      yield put(globalActions.displayToast(successString));
    }

    // Fetch messages and close form on successs
    yield put({ type: actions.REDEEM_RECEIPT_BARCODE_SUCCESS });
    yield put({ type: actions.FETCH_LOYALTY_STATE });
  } catch (error) {
    yield put({ type: actions.REDEEM_RECEIPT_BARCODE_FAILURE });

    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      'There was an error entering this receipt barcode.',
      error,
    );
    yield put(globalActions.displayErrorToast(errorResponse.message, true));

    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.REDEEM_BARCODE_ERROR,
    });
  }
}

export default function* rootSaga() {
  yield takeLatest(actions.FETCH_ME, fetchMeSaga);
  yield takeLatest(actions.FETCH_ME_FAVORITE_LOCATIONS, fetchMeFavoriteLocationsSaga);
  yield takeLatest(actions.ADD_FAVORITE_LOCATION, addFavoriteLocationSaga);
  yield takeLatest(actions.REMOVE_FAVORITE_LOCATION, deleteFavoriteLocationSaga);
  yield takeLatest(actions.FETCH_ME_FAVORITE_ORDERS, fetchMeFavoriteOrdersSaga);
  yield takeLatest(actions.ADD_FAVORITE_ORDER, addFavoriteOrderSaga);
  yield takeLatest(actions.REMOVE_FAVORITE_ORDER, deleteFavoriteOrderSaga);
  yield takeLatest(actions.FETCH_ME_PAST_ORDERS, fetchMePastOrdersSaga);
  yield takeLatest(actions.UPDATE_ME, updateMeSaga);
  yield takeLatest(actions.APPEND_USER_PHONE, appendUserPhoneSaga);
  yield takeLatest(actions.FETCH_ALL_LOCATIONS_CREDIT_CARDS, fetchAllLocationsCreditCardsSaga);
  yield takeLatest(actions.FETCH_ME_ORDER_DETAILS, fetchMeOrderDetailsSaga);
  yield takeLatest(actions.FETCH_OFFERS, fetchOffersSaga);
  // Punchh V2 only
  yield takeLatest(actions.FETCH_LOYALTY_STATE, fetchLoyaltyStateSaga);
  yield takeLatest(actions.FETCH_REDEEMABLES, fetchRedeemablesSaga);
  yield takeLatest(actions.FETCH_MESSAGES, fetchMessagesSaga);
  yield takeLatest(actions.FETCH_REWARDS_HISTORY, fetchRewardsHistorySaga);
  yield takeLatest(actions.MARK_MESSAGE_AS_READ, markMessageAsReadSaga);
  yield takeLatest(actions.DELETE_MESSAGE, deleteMessageSaga);
  yield takeLatest(actions.CREATE_REDEMPTION, createRedemptionSaga);
  yield takeLatest(actions.FETCH_REDEMPTIONS, fetchRedemptionsSaga);
  yield takeLatest(actions.REDEEM_RECEIPT_BARCODE, redeemReceiptBarcodeSaga);
  yield takeLatest(actions.FETCH_ADDRESSES, fetchAddressesSaga);
  yield takeLatest(actions.DELETE_ADDRESS, deleteAddressSaga);
  yield takeLatest(actions.DEFAULT_ADDRESS, defaultAddressSaga);
  yield takeLatest(actions.CREATE_REDEMPTION_SUCCESS, createRedemptionSagaSuccess);
  yield takeLatest(actions.CREATE_REDEMPTION_FAILURE, createRedemptionFailureSaga);
}
